import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import './Projects.css';
import ProjectsList from './ProjectsList/ProjectsList';
import ProjectView from './ProjectView/ProjectView';
import ProjectViewer from './ProjectViewer/ProjectViewer';
import { useProject } from '../../contexts/ProjectContext';
import { preloadVirtualTourMapping, preloadAllVirtualTours } from '../../utils/virtualTourMapping';

const Projects = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams(); // Get the project ID from URL params
  
  // Check if we're on a 3D view route
  const is3DView = location.pathname.endsWith('/view');
  const [currentView, setCurrentView] = useState('list'); // 'list', 'project', 'viewer'
  const [user, setUser] = useState(null);
  const [projectLoadTimeout, setProjectLoadTimeout] = useState(null);
  
  // Debug component lifecycle
  console.log('🔍 Projects Component - MOUNTED/RENDERED:', { 
    pathname: location.pathname, 
    id, 
    timestamp: new Date().toISOString() 
  });
  
  // Debug URL parameters immediately
  console.log('🔍 Projects Component - URL Debug:', {
    'location.pathname': location.pathname,
    'location.search': location.search,
    'location.hash': location.hash,
    'id from useParams': id,
    'id type': typeof id,
    'id === undefined': id === undefined,
    'id === null': id === null,
    'id === ""': id === '',
    'id value': id
  });
  
  // Debug navigation calls
  const originalNavigate = navigate;
  const debugNavigate = (...args) => {
    console.log('🔍 Projects - navigate called:', { args, currentPath: location.pathname, id });
    return originalNavigate(...args);
  };
  
  // Use project context
  const {
    projects,
    selectedProject,
    projectData,
    loading,
    error,
    loadProjects,
    areProjectsLoaded,
    setSelectedProject,
    clearError,
    clearSelectedProject
  } = useProject();

  useEffect(() => {
    // Load user info from session storage
    const userToken = sessionStorage.getItem('token');
    if (userToken) {
      try {
        const userData = JSON.parse(userToken);
        setUser(userData);
      } catch (error) {
        console.error('Error parsing user token:', error);
      }
    }

    // Preload virtual tour mapping data in the background
    preloadVirtualTourMapping().catch(error => {
      console.error('Error preloading virtual tour mapping:', error);
    });

    // Preload all virtual tours data in the background
    preloadAllVirtualTours().catch(error => {
      console.error('Error preloading virtual tours:', error);
    });

    // Load all projects - smart refresh will handle caching automatically
    // Only load if we don't already have projects
    if (!areProjectsLoaded()) {
      console.log('🔍 Loading projects...');
      loadProjects();
    } else {
      console.log('🔍 Projects already loaded:', projects?.length || 0, 'items');
    }
  }, [loadProjects, areProjectsLoaded]);

  // Handle URL changes to load specific projects
  useEffect(() => {
    console.log('🔍 URL change detected:', {
      pathname: location.pathname,
      projectId: id,
      projectIdType: typeof id,
      projectsLength: projects?.length || 0,
      projects: Array.isArray(projects) ? projects.map(p => ({ id: p.Id, name: p.Name })) : [],
      loading: loading,
      areProjectsLoaded: areProjectsLoaded()
    });
    
    // Additional debugging for projects array
    console.log('🔍 Projects Array Debug:', {
      'projects': projects,
      'projects type': typeof projects,
      'projects is array': Array.isArray(projects),
      'projects length': projects?.length,
      'projects === null': projects === null,
      'projects === undefined': projects === undefined
    });
    
    // Show actual project IDs
    if (projects && projects.length > 0) {
      console.log('🔍 Available Project IDs:', projects.map(p => ({ id: p.Id, name: p.Name, idType: typeof p.Id })));
    }
    
    // Additional debugging for URL parameter
    console.log('🔍 URL Parameter Debug:', {
      'id from useParams': id,
      'id type': typeof id,
      'id === undefined': id === undefined,
      'id === null': id === null,
      'id === ""': id === '',
      'id value': id
    });
    
    // Additional debugging for router state
    console.log('🔍 Router debugging:', {
      'location.pathname': location.pathname,
      'location.search': location.search,
      'location.hash': location.hash,
      'id from useParams': id,
      'id type': typeof id,
      'id === undefined': id === undefined,
      'id === null': id === null
    });

    // Check if URL contains a project ID (e.g., /projects/2405)
    if (id) {
      // Convert projectId to number for comparison
      const numericProjectId = parseInt(id, 10);
      
      console.log('🔍 PROJECT ID DETECTED:', {
        'id': id,
        'numericProjectId': numericProjectId,
        'is3DView': is3DView,
        'projectsLength': projects?.length || 0,
        'loading': loading,
        'areProjectsLoaded': areProjectsLoaded()
      });
      
      // Find the project with matching ID
      if (projects && projects.length > 0) {
        console.log('Searching for project ID:', numericProjectId, 'in projects:', projects.map(p => ({ id: p.Id, name: p.Name, idType: typeof p.Id })));
        console.log('Available project IDs:', projects.map(p => p.Id));
        console.log('Looking for ID type:', typeof numericProjectId, 'Value:', numericProjectId);
        console.log('Project ID types in array:', projects.map(p => ({ id: p.Id, type: typeof p.Id })));
        
        // Try both string and number comparison
        const project = projects.find(p => p.Id === numericProjectId || p.Id === id || p.Id === String(numericProjectId));
        console.log('Found project:', project);
        if (project) {
          // Project found, set it as selected and show appropriate view
          console.log('Setting selected project:', project.Name, 'is3DView:', is3DView);
          setSelectedProject(project);
          setCurrentView(is3DView ? 'viewer' : 'project');
          
          // Clear any existing timeout since we found the project
          if (projectLoadTimeout) {
            clearTimeout(projectLoadTimeout);
            setProjectLoadTimeout(null);
          }
        } else {
          // Project not found, redirect to projects list
          console.warn(`🔍 REDIRECT: Project with ID ${numericProjectId} not found. Available projects:`, projects.map(p => p.Id));
          console.warn(`🔍 REDIRECT: Navigating to /projects`);
          debugNavigate('/projects');
        }
      } else if (projects && projects.length === 0 && !loading) {
        // Projects loaded but empty array - project doesn't exist
        console.warn(`Project with ID ${numericProjectId} not found - no projects available`);
        // Don't redirect immediately, wait for projects to load
        console.log('Waiting for projects to load before redirecting...');
      } else {
        console.log('Projects array is empty or not loaded yet, waiting for projects to load...', {
          projectsLength: projects?.length || 0,
          loading: loading,
          areProjectsLoaded: areProjectsLoaded()
        });
        
        // Set a timeout to prevent infinite waiting
        if (!projectLoadTimeout) {
          console.log('Setting timeout for project load...');
          const timeout = setTimeout(() => {
            console.warn('Project load timeout reached, redirecting to projects list');
            debugNavigate('/projects');
          }, 10000); // 10 second timeout
          setProjectLoadTimeout(timeout);
        }
      }
    } else {
      // URL is just /projects, show projects list
      console.log('Showing projects list');
      setCurrentView('list');
      clearSelectedProject();
    }
  }, [id, location.pathname, setSelectedProject, clearSelectedProject, navigate, loading, areProjectsLoaded, projectLoadTimeout]);

  // Handle project loading when projects array changes
  useEffect(() => {
    if (id && projects && projects.length > 0) {
      const numericProjectId = parseInt(id, 10);
      const project = projects.find(p => p.Id === numericProjectId || p.Id === id || p.Id === String(numericProjectId));
      
      if (project) {
        console.log('🔍 PROJECT FOUND AFTER LOADING:', project.Name, 'is3DView:', is3DView);
        setSelectedProject(project);
        setCurrentView(is3DView ? 'viewer' : 'project');
        
        // Clear any existing timeout since we found the project
        if (projectLoadTimeout) {
          clearTimeout(projectLoadTimeout);
          setProjectLoadTimeout(null);
        }
      } else {
        console.warn(`🔍 PROJECT NOT FOUND AFTER LOADING: ${numericProjectId}. Available:`, projects.map(p => p.Id));
        debugNavigate('/projects');
      }
    }
  }, [projects, id, setSelectedProject, navigate, projectLoadTimeout, is3DView]);

  // Cleanup timeout on unmount or URL change
  useEffect(() => {
    return () => {
      console.log('🔍 Projects Component - UNMOUNTING:', { 
        pathname: location.pathname, 
        id, 
        timestamp: new Date().toISOString() 
      });
      if (projectLoadTimeout) {
        clearTimeout(projectLoadTimeout);
        setProjectLoadTimeout(null);
      }
    };
  }, [projectLoadTimeout, location.pathname, id]);

  const handleProjectSelect = (project) => {
    // Navigate to the project-specific URL
    debugNavigate(`/projects/${project.Id}`);
  };

  const handleViewProject = (project) => {
    console.log('🔍 Navigating to 3D view for project:', project.Id);
    debugNavigate(`/projects/${project.Id}/view`);
  };

  // Define custom redirect paths for specific projects
  const getCustomRedirectPaths = () => {
    // Example: You can customize redirect paths for specific project IDs
    // Return an object where keys are project IDs and values are the redirect URLs
    return {
      // Example redirects (replace with your actual project IDs and desired paths):
      // 123: '/custom-path-for-project-123',
      // 456: '/another-custom-path',
      // 789: 'https://external-site.com'
    };
  };

  const handleBackToList = () => {
    navigate('/projects');
    setCurrentView('list');
    clearSelectedProject();
  };

  const handleBackToProject = () => {
    if (selectedProject) {
      console.log('🔍 Navigating back to project view for:', selectedProject.Id);
      debugNavigate(`/projects/${selectedProject.Id}`);
    } else {
      navigate('/projects');
    }
  };

  const renderCurrentView = () => {
    switch (currentView) {
      case 'project':
        return (
          <ProjectView 
            project={selectedProject}
            projectData={projectData}
            onBack={handleBackToList}
            onViewProject={handleViewProject}
          />
        );
      case 'viewer':
        return (
          <ProjectViewer 
            project={selectedProject}
            onBack={handleBackToProject}
          />
        );
      default:
        return (
          <ProjectsList 
            projects={projects}
            loading={loading}
            onProjectSelect={handleProjectSelect}
            onViewProject={handleViewProject}
            onRefresh={() => loadProjects(true)}
            user={user}
            customRedirectPaths={getCustomRedirectPaths()}
          />
        );
    }
  };

  return (
    <div className="projects-container">
      {renderCurrentView()}
    </div>
  );
};

export default Projects;
